// ============================================================
// dev-core-web/src/components/ErrorBoundary.tsx
// Version: 1.2.0
// Purpose:
//   - React error boundary (class component)
//   - изолирует падение поддерева, рендерит безопасный fallback
//   - опциональный onError-колбэк для observability
//   - resetKeys для автоматического сброса при смене контекста
//     (route, entityId, queryKey и т.п.)
//   - НЕ делает security-решений; не логирует значения
//   - fallback не раскрывает internals (ADR-0003 §25.2)
//
// Правила:
//   - только class component (React так требует)
//   - никаких classified / PII / секретов в тексте fallback
//   - никаких console.log по умолчанию (опционально через onError)
//   - resetKeys сравниваются по значению (shallow), не по ссылке
//   - fallback — ReactNode | render-prop
//
// Ссылки:
//   - docs/SECURITY.md §19.3 (no sensitive logging)
//   - docs/adr/0003-rbac-vs-rebac.md §25.2 (safe reason codes)
//   - docs/CLASSIFICATION.md §14 (no classified in UI)
//   - docs/MONITORING.md §20 (observability)
//   - docs/DEVELOPMENT.md §11.2, §11.3
// ============================================================

import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// ------------------------------------------------------------
// Типы
// ------------------------------------------------------------

export interface ErrorBoundaryProps {
  /** Дочернее дерево, которое защищаем. */
  children: ReactNode;

  /**
   * Кастомный fallback.
   *   - ReactNode — рендерится как есть;
   *   - render-prop — получает error + reset, возвращает ReactNode.
   *
   * Если не задан — используется встроенный DefaultFallback.
   */
  fallback?:
    | ReactNode
    | ((args: { error: Error; reset: () => void }) => ReactNode);

  /**
   * Колбэк при пойманной ошибке.
   *
   * ВАЖНО: НЕ логировать `error.message` в аналитику, если есть
   * риск PII/classified. Здесь уместно отправить только
   * `error.name` + stack в защищённый collector (Sentry и т.п.).
   */
  onError?: ((error: Error, info: ErrorInfo) => void) | undefined;

  /**
   * Сброс boundary при изменении значений.
   *
   * Стандартный паттерн: если пользователь перешёл на другую
   * страницу (route) или entityId изменился — boundary должен
   * «забыть» прошлую ошибку и попробовать снова.
   *
   * Сравнение — shallow (по `Object.is`), не глубокое.
   */
  resetKeys?: readonly unknown[] | undefined;

  /**
   * Заголовок для встроенного fallback. По умолчанию — «Что-то
   * пошло не так». Не должен содержать sensitive-данных.
   */
  fallbackTitle?: string | undefined;

  /**
   * Описание для встроенного fallback. По умолчанию —
   * нейтральное. Не должно содержать sensitive-данных.
   */
  fallbackDescription?: string | undefined;

  /** Дополнительный className для встроенного fallback. */
  className?: string | undefined;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

/**
 * Shallow-сравнение массивов resetKeys.
 * `Object.is` — корректно работает с NaN, -0, примитивами,
 * и по ссылке для объектов.
 */
function shallowEqualKeys(
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

/**
 * Нормализуем error: React может поймать не только Error,
 * но и брошенное значение (throw 'string'). Приводим к Error.
 */
function normalizeError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  return new Error('Unknown error');
}

// ------------------------------------------------------------
// ErrorBoundary (class)
// ------------------------------------------------------------

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public override state: ErrorBoundaryState = { error: null };

  // ----------------------------------------------------------
  // Lifecycle
  // ----------------------------------------------------------

  public static getDerivedStateFromError(
    error: unknown,
  ): ErrorBoundaryState {
    return { error: normalizeError(error) };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Не логируем сами (SECURITY.md §19.3).
    // Отдаём наверх — caller решает, что безопасно отправлять.
    this.props.onError?.(error, info);
  }

  public override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Сброс boundary при смене resetKeys.
    if (this.state.error === null) return;
    if (
      !shallowEqualKeys(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  // ----------------------------------------------------------
  // Public API
  // ----------------------------------------------------------

  public reset = (): void => {
    this.setState({ error: null });
  };

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------

  public override render(): ReactNode {
    const { error } = this.state;
    const {
      children,
      fallback,
      fallbackTitle,
      fallbackDescription,
      className,
    } = this.props;

    if (error === null) {
      return children;
    }

    // Render-prop fallback.
    if (typeof fallback === 'function') {
      return fallback({ error, reset: this.reset });
    }

    // ReactNode fallback.
    if (fallback !== undefined) {
      return fallback;
    }

    // Встроенный fallback.
    return (
      <DefaultFallback
        error={error}
        onReset={this.reset}
        title={fallbackTitle}
        description={fallbackDescription}
        className={className}
      />
    );
  }
}

// ------------------------------------------------------------
// Default fallback
// ------------------------------------------------------------

interface DefaultFallbackProps {
  error: Error;
  onReset: () => void;
  title?: string | undefined;
  description?: string | undefined;
  className?: string | undefined;
}

function DefaultFallback({
  onReset,
  title,
  description,
  className,
}: DefaultFallbackProps) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className={[
        'flex flex-col items-center justify-center gap-3 rounded-lg',
        'border border-dashed border-amber-200 bg-amber-50/50 p-6 text-center',
        'dark:border-amber-800 dark:bg-amber-950/20',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
        <AlertTriangle className="h-5 w-5" aria-hidden />
      </div>

      <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
        {title ?? 'Что-то пошло не так'}
      </div>

      <div className="max-w-md text-xs text-amber-800 dark:text-amber-300">
        {description ??
          'Раздел не удалось отобразить. Попробуйте обновить или повторить действие позже.'}
      </div>

      <button
        type="button"
        onClick={onReset}
        className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
        Попробовать снова
      </button>

      {/*
        Намеренно НЕ показываем error.message.
        Это может содержать: пути, id, фрагменты payload,
        данные от API — потенциально sensitive.
        Диагностика идёт через onError → observability.
      */}
    </div>
  );
}

// ------------------------------------------------------------
// HOC: withErrorBoundary
// ------------------------------------------------------------
//
// Удобно для обёртки страниц или экспортируемых компонентов:
//   export default withErrorBoundary(MyPage, { ... })
//
// Сохраняет displayName и не теряет ref (forwardRef).

export type WithErrorBoundaryOptions = Omit<ErrorBoundaryProps, 'children'>;

export function withErrorBoundary<P extends object>(
  Wrapped: React.ComponentType<P>,
  options: WithErrorBoundaryOptions = {},
): React.ComponentType<P> {
  const { fallback, onError, resetKeys, fallbackTitle, fallbackDescription, className } =
    options;

  const WithBoundary = (props: P): ReactNode => (
    <ErrorBoundary
      fallback={fallback}
      onError={onError}
      resetKeys={resetKeys}
      fallbackTitle={fallbackTitle}
      fallbackDescription={fallbackDescription}
      className={className}
    >
      <Wrapped {...props} />
    </ErrorBoundary>
  );

  const name =
    Wrapped.displayName || Wrapped.name || 'Component';
  WithBoundary.displayName = `withErrorBoundary(${name})`;

  return WithBoundary;
}

// ------------------------------------------------------------
// Re-exports
// ------------------------------------------------------------

export type { ErrorInfo };

export default ErrorBoundary;